import { computed, onMounted, onUnmounted, ref } from 'vue'
import { getAdminPendingNotifications } from '@/api'
import {
  connectAdminNotificationStream,
  disconnectAdminNotificationStream,
} from '@/utils/adminNotificationStream.js'

const DISMISS_STORAGE_KEY = 'admin.notifications.dismissed.v1'
/** SSE 正常时作兜底；断线时仍能 eventually 刷新 */
const POLL_MS = 120_000
/** 列表可滚动，不再截断；可视高度约 2 张卡片（样式控制） */
const MAX_VISIBLE = 200

function readDismissedSet() {
  try {
    const raw = localStorage.getItem(DISMISS_STORAGE_KEY) || '[]'
    const arr = JSON.parse(raw)
    return new Set(Array.isArray(arr) ? arr.map(String) : [])
  } catch {
    return new Set()
  }
}

function writeDismissedSet(set) {
  try {
    localStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify([...set]))
  } catch {
    /* ignore */
  }
}

const pendingAll = ref([])
const dismissed = ref(readDismissedSet())
const loading = ref(false)
const lastError = ref('')
let pollTimer = null
let mountedCount = 0

/** 当前待办总数（与消息中心列表一致；关闭弹窗不计为已处理）。 */
const unreadCount = computed(() => {
  const list = pendingAll.value
  return Array.isArray(list) ? list.length : 0
})

async function fetchPending() {
  loading.value = true
  lastError.value = ''
  try {
    const list = (await getAdminPendingNotifications()) || []
    pendingAll.value = Array.isArray(list) ? list : []
    const liveIds = new Set(pendingAll.value.map((x) => String(x.id || '')))
    const next = new Set(dismissed.value)
    for (const id of [...next]) {
      if (!liveIds.has(id)) {
        next.delete(id)
      }
    }
    dismissed.value = next
    writeDismissedSet(next)
  } catch (e) {
    lastError.value = e?.message || '待办加载失败'
  } finally {
    loading.value = false
  }
}

function startPolling() {
  if (pollTimer != null) return
  pollTimer = window.setInterval(() => {
    if (document.visibilityState === 'visible') {
      void fetchPending()
    }
  }, POLL_MS)
}

function stopPolling() {
  if (pollTimer != null) {
    window.clearInterval(pollTimer)
    pollTimer = null
  }
}

function onVisibilityChange() {
  if (document.visibilityState === 'visible') {
    void fetchPending()
  }
}

function startRealtime() {
  connectAdminNotificationStream(() => {
    void fetchPending()
  })
}

function stopRealtime() {
  disconnectAdminNotificationStream()
}

export { pendingAll, loading, lastError, unreadCount, fetchPending as refresh }

export function useAdminNotifications() {
  onMounted(() => {
    mountedCount += 1
    if (mountedCount === 1) {
      void fetchPending()
      startRealtime()
      startPolling()
      document.addEventListener('visibilitychange', onVisibilityChange)
    }
  })
  onUnmounted(() => {
    mountedCount = Math.max(0, mountedCount - 1)
    if (mountedCount === 0) {
      stopRealtime()
      stopPolling()
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  })

  const visibleAll = computed(() =>
    pendingAll.value.filter((item) => {
      const id = String(item?.id || '')
      return id && !dismissed.value.has(id)
    }),
  )

  const visibleItems = computed(() => visibleAll.value.slice(0, MAX_VISIBLE))

  /** 超过约 2 张可视高度时提示可滚动 */
  const overflowCount = computed(() => Math.max(0, visibleAll.value.length - 2))

  function dismiss(id) {
    const key = String(id || '').trim()
    if (!key) return
    const next = new Set(dismissed.value)
    next.add(key)
    dismissed.value = next
    writeDismissedSet(next)
  }

  function dismissAll() {
    const next = new Set(dismissed.value)
    for (const item of pendingAll.value) {
      const id = String(item?.id || '').trim()
      if (id) next.add(id)
    }
    dismissed.value = next
    writeDismissedSet(next)
  }

  return {
    visibleItems,
    visibleAll,
    overflowCount,
    unreadCount,
    loading,
    lastError,
    refresh: fetchPending,
    dismiss,
    dismissAll,
  }
}
